/*
  # ScrollView Component:
  A reusable and accessible scroll view component that wraps React Native's `ScrollView`
  with consistent styling patterns and enhanced accessibility support.

  ## Features
  - Wraps React Native's `ScrollView` with MADS styling conventions
  - Supports all standard ScrollView props
  - Uses `sx` prop for styling consistency with other MADS components
  - Uses `contentContainerSx` for content container styling
  - Provides a foundation for future React Strict DOM migration

  ## Usage

  ```tsx
  import { ScrollView } from '@indeed/mads';

  <ScrollView
    sx={{ flex: 1 }}
    contentContainerSx={{ padding: 16 }}
  >
    <Text>Scrollable content</Text>
  </ScrollView>
  ```
 */

import React from "react";
import {
  ScrollView as RNScrollView,
  type ScrollViewProps as RNScrollViewProps,
  type StyleProp,
  type ViewStyle,
} from "react-native";

/**
 * Re-export the ScrollView type for use with refs.
 * This allows consumers to type their refs without importing from react-native directly.
 */
export type ScrollViewRef = RNScrollView;

export interface ScrollViewProps extends Omit<
  RNScrollViewProps,
  "style" | "contentContainerStyle"
> {
  /**
   * Style prop for the ScrollView container using MADS convention.
   */
  readonly sx?: StyleProp<ViewStyle>;

  /**
   * Style prop for the content container using MADS convention.
   */
  readonly contentContainerSx?: StyleProp<ViewStyle>;

  /**
   * Children to render inside the scroll view
   */
  readonly children?: React.ReactNode;

  /**
   * Ref to the underlying ScrollView component
   */
  readonly ref?: React.Ref<RNScrollView>;
}

/**
 * ScrollView component that wraps React Native's ScrollView with MADS styling conventions.
 * Use this component for scrollable content areas.
 */
export function ScrollView({
  sx,
  contentContainerSx,
  children,
  ref,
  ...props
}: ScrollViewProps): React.JSX.Element {
  return (
    <RNScrollView ref={ref} style={sx} contentContainerStyle={contentContainerSx} {...props}>
      {children}
    </RNScrollView>
  );
}
